import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheckCircle, FaArrowRight, FaTimes } from 'react-icons/fa';
import { NudgeKey, readNudge, clearNudge } from '../../utils/nudges';

interface NextStepNudgeProps {
  // Which nudge to look for — one of the keys defined in utils/nudges.ts.
  // The list page that renders this passes its own key, so a stale nudge
  // from a different entity flow doesn't appear in the wrong place.
  forKey: NudgeKey;
}

// In-page banner shown after an admin returns from a successful create.
// Reads the sessionStorage flag set by markCreated(); renders nothing if
// no flag is present or it has expired.
const NextStepNudge: React.FC<NextStepNudgeProps> = ({ forKey }) => {
  const navigate = useNavigate();
  const [content, setContent] = useState(() => readNudge(forKey));

  // Re-check on mount in case the page was opened in a different tab.
  useEffect(() => {
    setContent(readNudge(forKey));
  }, [forKey]);

  if (!content) return null;

  const dismiss = () => {
    clearNudge(forKey);
    setContent(null);
  };

  const goNext = () => {
    clearNudge(forKey);
    setContent(null);
    navigate(content.actionTo);
  };

  return (
    <div className="mb-4 p-4 rounded-lg border border-emerald-200 bg-emerald-50 flex items-start gap-3">
      <FaCheckCircle className="text-emerald-600 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-emerald-900">{content.message}</p>
        <button
          type="button"
          onClick={goNext}
          className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 hover:text-emerald-900"
        >
          {content.actionLabel}
          <FaArrowRight className="text-xs" />
        </button>
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss"
        className="text-emerald-400 hover:text-emerald-700 shrink-0"
      >
        <FaTimes />
      </button>
    </div>
  );
};

export default NextStepNudge;
